import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { MapPin, Phone, Mail, Clock, Send, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PageView = 'home' | 'archive' | 'detail' | 'configurator' | 'warranty' | 'instructions' | 'contact';
interface ContactPageProps {
  onNavigate: (page: PageView, productId?: number) => void;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const update = (field: string, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-303">
      <Header data-id="element-304" />
      <Navigation onNavigate={onNavigate} data-id="element-305" />

      <main className="flex-grow" data-id="element-306">
        {/* Hero */}
        <div className="bg-charcoal text-white py-16 relative overflow-hidden" data-id="element-307">
          <div className="absolute inset-0 bg-hero-pattern opacity-10 bg-cover bg-center" data-id="element-308" />
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" data-id="element-309" />
          <div className="max-w-[1400px] mx-auto px-4 lg:px-8 relative z-10" data-id="element-310">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-311">
              <button onClick={() => onNavigate('home')} className="hover:text-primary transition-colors" data-id="element-312">
                Home
              </button>
              <span data-id="element-313">/</span>
              <span className="text-white" data-id="element-314">Contact</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-bold uppercase italic tracking-tighter" data-id="element-315">
              Get In <span className="text-primary" data-id="element-316">Touch</span>
            </h1>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16" data-id="element-317">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12" data-id="element-318">
            {/* Contact Info — Left Column */}
            <div className="lg:col-span-2 space-y-8" data-id="element-319">
              <div data-id="element-320">
                <h2 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-2" data-id="element-321">
                  Pro One Design
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed" data-id="element-322">
                  We're a local manufacturer based in Fresno, California.
                  Whether you need help choosing the right kit or have a
                  question about an existing order, we're here for you.
                </p>
              </div>

              <div className="space-y-5" data-id="element-323">
                <div className="flex items-start gap-4" data-id="element-324">
                  <div className="w-10 h-10 bg-charcoal flex items-center justify-center flex-shrink-0" data-id="element-325">
                    <MapPin className="w-5 h-5 text-primary" data-id="element-326" />
                  </div>
                  <div data-id="element-327">
                    <h4 className="font-bold text-charcoal text-sm uppercase" data-id="element-328">
                      Address
                    </h4>
                    <p className="text-gray-500 text-sm" data-id="element-329">
                      123 Offroad Way
                      <br data-id="element-330" />
                      Fresno, CA 93727
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-331">
                  <div className="w-10 h-10 bg-charcoal flex items-center justify-center flex-shrink-0" data-id="element-332">
                    <Phone className="w-5 h-5 text-primary" data-id="element-333" />
                  </div>
                  <div data-id="element-334">
                    <h4 className="font-bold text-charcoal text-sm uppercase" data-id="element-335">
                      Phone
                    </h4>
                    <a href="tel:5591234567" className="text-gray-500 text-sm hover:text-primary transition-colors" data-id="element-336">
                      (559) 123-4567
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-337">
                  <div className="w-10 h-10 bg-charcoal flex items-center justify-center flex-shrink-0" data-id="element-338">
                    <Mail className="w-5 h-5 text-primary" data-id="element-339" />
                  </div>
                  <div data-id="element-340">
                    <h4 className="font-bold text-charcoal text-sm uppercase" data-id="element-341">
                      Email
                    </h4>
                    <a href="mailto:sales@proonedesign.com" className="text-gray-500 text-sm hover:text-primary transition-colors" data-id="element-342">
                      sales@proonedesign.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-343">
                  <div className="w-10 h-10 bg-charcoal flex items-center justify-center flex-shrink-0" data-id="element-344">
                    <Clock className="w-5 h-5 text-primary" data-id="element-345" />
                  </div>
                  <div data-id="element-346">
                    <h4 className="font-bold text-charcoal text-sm uppercase" data-id="element-347">
                      Hours
                    </h4>
                    <p className="text-gray-500 text-sm" data-id="element-348">
                      Mon – Fri: 8:00 AM – 5:00 PM
                      <br data-id="element-349" />
                      Sat – Sun: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="w-full h-56 bg-charcoal relative overflow-hidden" data-id="element-350">
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-351">
                  <div className="text-center" data-id="element-352">
                    <MapPin className="w-8 h-8 text-primary mx-auto mb-2" data-id="element-353" />
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-wider" data-id="element-354">
                      Fresno, California
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '20px 20px'
              }} data-id="element-355" />
              </div>
            </div>

            {/* Contact Form — Right Column */}
            <div className="lg:col-span-3" data-id="element-356">
              <AnimatePresence mode="wait" data-id="element-357">
                {!submitted ? <motion.form key="form" initial={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8" data-id="element-358">
                    <h2 className="text-2xl font-heading font-bold text-charcoal uppercase italic mb-6" data-id="element-359">
                      Send Us a Message
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5" data-id="element-360">
                      <div data-id="element-361">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-362">
                          Name *
                        </label>
                        <input type="text" required value={form.name} onChange={e => update('name', e.target.value)} className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors bg-gray-50" placeholder="John Doe" data-id="element-363" />
                      </div>
                      <div data-id="element-364">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-365">
                          Email *
                        </label>
                        <input type="email" required value={form.email} onChange={e => update('email', e.target.value)} className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors bg-gray-50" placeholder="john@example.com" data-id="element-366" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5" data-id="element-367">
                      <div data-id="element-368">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-369">
                          Phone
                        </label>
                        <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors bg-gray-50" placeholder="(555) 123-4567" data-id="element-370" />
                      </div>
                      <div data-id="element-371">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-372">
                          Subject *
                        </label>
                        <select required value={form.subject} onChange={e => update('subject', e.target.value)} className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors bg-gray-50 appearance-none" data-id="element-373">
                          <option value="" data-id="element-374">Select a topic...</option>
                          <option value="sales" data-id="element-375">Sales Inquiry</option>
                          <option value="support" data-id="element-376">Technical Support</option>
                          <option value="warranty" data-id="element-377">Warranty Claim</option>
                          <option value="install" data-id="element-378">Installation Help</option>
                          <option value="dealer" data-id="element-379">Dealer Inquiry</option>
                          <option value="other" data-id="element-380">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-381">
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-382">
                        Message *
                      </label>
                      <textarea required rows={5} value={form.message} onChange={e => update('message', e.target.value)} className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors bg-gray-50 resize-none" placeholder="Tell us about your vehicle, what you're looking for, or describe your issue..." data-id="element-383" />
                    </div>
                    <button type="submit" className="w-full py-4 bg-primary text-white font-heading font-bold text-xl uppercase italic tracking-wider hover:bg-charcoal transition-colors skew-x-[-10deg]" data-id="element-384">
                      <span className="block skew-x-[10deg] flex items-center justify-center gap-2" data-id="element-385">
                        <Send className="w-5 h-5" data-id="element-386" /> Send Message
                      </span>
                    </button>
                  </motion.form> : <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="bg-white border border-gray-200 p-12 text-center" data-id="element-387">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-388">
                      <Check className="w-10 h-10 text-green-600" data-id="element-389" />
                    </div>
                    <h2 className="text-3xl font-heading font-bold text-charcoal uppercase italic mb-3" data-id="element-390">
                      Message Sent
                    </h2>
                    <p className="text-gray-500 mb-8 max-w-md mx-auto" data-id="element-391">
                      Thanks for reaching out,{' '}
                      {form.name.split(' ')[0] || 'friend'}. We typically
                      respond within 1 business day. Check your email at{' '}
                      <strong data-id="element-392">{form.email}</strong> for our reply.
                    </p>
                    <button onClick={() => {
                  setSubmitted(false);
                  setForm({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                  });
                }} className="px-8 py-3 bg-charcoal text-white font-heading font-bold uppercase italic tracking-wider hover:bg-primary transition-colors skew-x-[-10deg]" data-id="element-393">
                      <span className="block skew-x-[10deg]" data-id="element-394">
                        Send Another Message
                      </span>
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-395" />
    </div>;
}